import { DatePipe } from "@angular/common";
import { Component, EventEmitter, inject, Input, OnInit, Output, ViewChild } from "@angular/core";
import { DateRange, MatDatepicker } from "@angular/material/datepicker";
import { addAsterisk } from "../../shared/utils/helper-string";

type BorderType = "left" | "right";
const DEFAULT_DATE_TYPE = "yyyy-MM-dd";

@Component({
  selector: "app-date-picker",
  templateUrl: "./date-picker.component.html",
})
export class DatePickerComponent implements OnInit {
  @Input() isDisabled = false;
  @Input() label: string;
  @Input() labelClass: string;
  @Input() dateInputLabel: string;
  @Input() isRequired: boolean;
  @Input() hasIcon = true;
  /** Compact variant: ~24px tall, neumorphic inset field, small text, no calendar icon. Used for inline rows. */
  @Input() compact = false;
  @Input() hasError: boolean; // todo remove
  /** When false, the error state shows the red border only (no helper text). Keeps
   *  inline rows from changing height when the "required" message would appear. */
  @Input() showErrorText: boolean = true;
  @Input() dateFormat = DEFAULT_DATE_TYPE;
  @Input() borderType: BorderType;
  @Input() dateText: string;
  @Input() selectedDateRange: DateRange<Date>;
  @Input() restrictDirection: "left" | "right" | "both" = "both";
  @Input() restrictToFuture: boolean = false;
  @Input() dateLabel: string;
  @Output() changeDateEvent: EventEmitter<any> = new EventEmitter();

  @ViewChild("datePicker") datePicker: MatDatepicker<any>;
  private datePipe = inject(DatePipe);

  ngOnInit() {
    this.dateLabel = this.label ? addAsterisk(this.label, this.isRequired) : "";
    this.setDateText(this.dateText);
  }

  openDatePicker() {
    if (!this.isDisabled) {
      this.datePicker.open();
    }
  }

  filterDates = (d: Date | null): boolean => {
    const today = new Date();
    if (!d) {
      return false;
    }

    // Only selectedDateRange present
    if (this.selectedDateRange && this.restrictDirection === "both") {
      const startDate = new Date(this.selectedDateRange.start);
      const endDate = new Date(this.selectedDateRange.end);
      const startDateResetTime = new Date(
        startDate.getFullYear(),
        startDate.getMonth(),
        startDate.getDate(),
      );
      const endDateResetTime = new Date(
        endDate.getFullYear(),
        endDate.getMonth(),
        endDate.getDate(),
      );

      return d >= startDateResetTime && d <= endDateResetTime;
    }

    // all variables present
    if (this.selectedDateRange && this.restrictDirection) {
      // For end picker (restrictDirection="left" means restrict to dates >= start)
      if (this.restrictDirection === "left") {
        const startDate = new Date(
          this.selectedDateRange.start.getFullYear(),
          this.selectedDateRange.start.getMonth(),
          this.selectedDateRange.start.getDate(),
        );
        return d >= startDate && d <= today;
      }
      // For start picker (restrictDirection="right" means restrict to dates <= end)
      if (this.restrictDirection === "right") {
        const endDate = new Date(
          this.selectedDateRange.end.getFullYear(),
          this.selectedDateRange.end.getMonth(),
          this.selectedDateRange.end.getDate(),
        );
        return d <= endDate;
      }
    }
    // Default
    if (this.restrictToFuture) {
      const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
      return d >= startOfToday;
    }
    return d <= today;
  };

  onDateChange(event: any) {
    this.setDateText(event.value);
    // todo: should emit the date instead
    this.changeDateEvent.emit(this.dateText);
  }

  resetDate() {
    this.dateText = "";
  }

  setDateText(date?: string) {
    if (!date && !this.selectedDateRange?.start) {
      this.dateText = "";
      return;
    }
    const dateToFormat = date || this.selectedDateRange.start;
    this.dateText = this.datePipe.transform(dateToFormat, this.dateFormat);
  }

  // todo: if need to support dateRange when dateText is not present change here( but then it will get too complicated might as well change the calendar component to support this)
  getDatePickerValue(): Date | null {
    return this.dateText ? new Date(this.dateText) : null;
  }
}
